// dependencies
import React, { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { XCircle } from "lucide-react";

// components
import { getRebacPolicies, upsertRebacPolicy, deleteRebacPolicy } from "../../../../../Api/services/access-controls";
import Grid from "../../../../../Components/Grid";
import { UserSelector } from "../../../../../Components/Selectors/UserSelector";
import { PermissionSelector } from "../../../../../Components/Selectors/PermissionSelector";
import Button from "../../../../../Components/Button";
import { Text } from "../../../../../Components/Text";

// styles
import css from "./access-controls.styl";

const usePolicies = (companyId) => {
	return useQuery({
		queryKey: ["performyard-resource-policies", "company", companyId],
		queryFn: () => getRebacPolicies("company", companyId),
		refetchInterval: 30_000,
	});
};

const AddNewAccessForm = (props) => {
	const { companyID } = props;

	const [user, setUser] = useState(null);
	const [role, setRole] = useState(null);

	const { refetch } = usePolicies(companyID);

	const handleAdd = async () => {
		if (!user || !role) {
			return;
		}

		await upsertRebacPolicy({
			resourceType: "company",
			resourceId: companyID,
			relationship: role.value,
			userId: user.value,
		});

		refetch();
	};

	return (
		<div>
			<Grid gap={24} columns={["1fr", "1fr", "80px"]}>
				<div>
					<Text as="label" variant="caption" weight="bold" color="#666">
						User
					</Text>
					<UserSelector onChange={(value) => setUser(value)} />
				</div>

				<div>
					<Text as="label" variant="caption" weight="bold" color="#666">
						Role
					</Text>
					<PermissionSelector resource="company" onChange={(value) => setRole(value)} />
				</div>

				<div>
					<div style={{ height: 22 }} />
					<Button onClick={handleAdd}>Add</Button>
				</div>
			</Grid>
		</div>
	);
};

const ExistingAccessList = (props) => {
	const { companyID } = props;

	const { data, refetch } = usePolicies(companyID);

	const handleDeletePolicy = async (policyId) => {
		await deleteRebacPolicy(policyId);
		refetch();
	};

	/**
	 * Group policies by role (relationship).
	 */
	const policiesByRole = useMemo(() => {
		const policies = data ?? [];

		return policies.reduce((acc, policy) => {
			const { relationship } = policy;

			if (!acc[relationship]) {
				acc[relationship] = [];
			}

			acc[relationship].push(policy);

			return acc;
		}, {});
	}, [data]);

	return (
		<div>
			{Object.entries(policiesByRole).map(([role, policies]) => {
				return (
					<div className={css.policy} key={role}>
						<div className={css.policyName}>{role}</div>
						<div>
							{policies.map((policy) => {
								return (
									<div className={css.policyItem} key={policy._id}>
										<div className={css.owner}>{policy.user.displayName}</div>
										<div
											onClick={() => handleDeletePolicy(policy._id)}
											onKeyDown={(e) => e.key === "Enter" && handleDeletePolicy(policy._id)}
											role="button"
											tabIndex={0}
											className={css.delete}
										>
											<XCircle size={16} />
										</div>
									</div>
								);
							})}
						</div>
					</div>
				);
			})}
		</div>
	);
};

const AccessControls = (props) => {
	const { companyID } = props;

	return (
		<div>
			<AddNewAccessForm companyID={companyID} />
			<ExistingAccessList companyID={companyID} />
		</div>
	);
};

export default AccessControls;
